import { theme } from '@energiebespaarders/symbols';
import { padding } from '@energiebespaarders/symbols/styles/mixins';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import React, { useCallback } from 'react';
import { default as SlickSlider } from 'react-slick';
import styled from 'styled-components';
import type { CustomArrowProps, Settings } from 'react-slick';

const StyledSlider = styled(SlickSlider)`
  .slick-list,
  .slick-slider,
  .slick-track {
    position: relative;
    display: flex;
  }
  .slick-loading .slick-slide,
  .slick-loading .slick-track {
    visibility: hidden;
  }
  .slick-slider {
    box-sizing: border-box;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -khtml-user-select: none;
    -ms-touch-action: pan-y;
    touch-action: pan-y;
    -webkit-tap-highlight-color: transparent;
  }
  .slick-list {
    overflow: hidden;
    margin: 0;
    padding: 0;
  }
  .slick-list:focus {
    outline: 2px solid dodgerblue;
    outline-offset: 2px;
  }
  .slick-list.dragging {
    cursor: pointer;
    cursor: hand;
  }
  .slick-slider .slick-list,
  .slick-slider .slick-track {
    transform: translate3d(0, 0, 0);
  }
  .slick-track {
    top: 0;
    left: 0;
  }
  .slick-track:after,
  .slick-track:before {
    display: table;
    content: '';
  }
  .slick-track:after {
    clear: both;
  }
  .slick-track .slick-slide {
    display: flex;
    height: auto;
    align-items: center;
    justify-content: center;
  }
  .slick-slide {
    display: none;
    float: left;
    height: 100%;
    min-height: 1px;
    ${padding(2, 'x')};
    ${padding(2, 'y')};

    & *:focus {
      outline: 2px solid dodgerblue;
      outline-offset: 2px;
    }
  }
  [dir='rtl'] .slick-slide {
    float: right;
  }
  .slick-slide.slick-loading img {
    display: none;
  }
  .slick-slide.dragging img {
    pointer-events: none;
  }
  .slick-initialized .slick-slide {
    display: flex;
    height: auto;
    align-items: center;
    justify-content: center;
  }
  .slick-vertical .slick-slide {
    display: flex;
    height: auto;
    align-items: center;
    justify-content: center;
    border: 1px solid transparent;
  }
  .slick-arrow.slick-hidden {
    display: none;
  }

  .slick-next,
  .slick-prev {
    position: absolute;
    display: block;
    padding: 0;
    z-index: 1;
    width: 40px;
    height: 40px;
    background: ${x => x.theme.colors.grayLighter};
    border: 1px solid ${x => x.theme.colors.grayLight};
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:focus {
      outline: 2px solid dodgerblue;
      outline-offset: 2px;
    }

    &:hover {
      background: ${x => x.theme.colors.grayLight};
    }
  }
  .slick-next,
  .slick-prev {
    font-size: 0;
    line-height: 0;
    top: 50%;
    transform: translate(0, -50%);
    color: transparent;
    border: none;
    background: 0 0;
  }
  .slick-next:focus,
  .slick-next:hover,
  .slick-prev:focus,
  .slick-prev:hover {
    color: transparent;
    background: 0 0;

    &::before {
      opacity: 1;
    }
  }
  .slick-next.slick-disabled:before,
  .slick-prev.slick-disabled:before {
    opacity: 0.25;
  }
  .slick-next:before,
  .slick-prev:before {
    font-size: 20px;
    line-height: 1;
    opacity: 0.75;
    color: #fff;
  }
  .slick-prev {
    left: -6px;
  }
  [dir='rtl'] .slick-prev {
    right: 0;
    left: auto;
  }
  .slick-next {
    right: -6px;
  }
  [dir='rtl'] .slick-next {
    right: auto;
    left: 0;
  }
  .slick-dotted.slick-slider {
    margin-bottom: 30px;
  }
  .slick-dots {
    width: 100%;
    margin: 0;
    list-style: none;
    text-align: center;
  }
  .slick-dots li {
    position: relative;
    display: inline-block;
    width: 20px;
    height: 20px;
    margin: 0 5px;
    padding: 0;
    cursor: pointer;

    &::before {
      color: ${x => x.theme.colors.gray};
    }

    &.slick-active {
      &::before {
        color: ${x => x.theme.colors.green};
      }
    }

    button {
      font-size: 0;
      line-height: 0;
      display: block;
      width: 20px;
      height: 20px;
      padding: 5px;
      cursor: pointer;
      color: transparent;
      border: 0;
      outline: 0;
      background: transparent;

      &:focus {
        outline: 2px solid ${x => x.theme.colors.greenDark};
        outline-offset: 2px;
      }
    }
  }
`;

const StyledArrowButton = styled.button`
  outline: none;

  &:hover {
    * {
      color: ${x => x.theme.colors.greenDark};
    }
  }

  &:focus {
    outline: 2px solid dodgerblue;
    border-radius: 2px;
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: 2px solid dodgerblue;
    border-radius: 2px;
    outline-offset: 2px;
  }
`;

const PrevArrow = ({ currentSlide, slideCount, ...props }: CustomArrowProps) => (
  <StyledArrowButton {...props} className="slick-prev" aria-label="Previous slide" type="button">
    <ArrowLeft size={24} weight="bold" color={theme.colors.grayDarker} />
  </StyledArrowButton>
);

const NextArrow = ({ currentSlide, slideCount, ...props }: CustomArrowProps) => (
  <StyledArrowButton {...props} className="slick-next" aria-label="Next slide" type="button">
    <ArrowRight size={24} weight="bold" color={theme.colors.grayDarker} />
  </StyledArrowButton>
);

interface SliderProps extends Settings {
  children: React.ReactNode;
  label?: string;
}

const Slider: React.FC<SliderProps> = ({ children, label = 'Image carousel', ...settings }) => {
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    const slider = event.currentTarget;
    const activeElement = document.activeElement;

    if (activeElement) {
      const prevButton = slider.querySelector('.slick-prev') as HTMLButtonElement;
      const nextButton = slider.querySelector('.slick-next') as HTMLButtonElement;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          prevButton?.click();
          break;
        case 'ArrowRight':
          event.preventDefault();
          nextButton?.click();
          break;
      }
    }
  }, []);

  return (
    <div role="region" aria-label={label} aria-roledescription="carousel" onKeyDown={handleKeyDown}>
      <StyledSlider {...settings} prevArrow={<PrevArrow />} nextArrow={<NextArrow />}>
        {children}
      </StyledSlider>
    </div>
  );
};

export default Slider;
